'use client';
import React, { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const headRef = useRef<HTMLDivElement>(null);

  const [fields, setFields] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    service: '',
    targetRole: '',
    linkedin: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    if (headRef.current) observer.observe(headRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!allowed.includes(file.type)) {
      setError('Please upload a PDF, DOC, DOCX, or TXT file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File must be under 10 MB.');
      return;
    }
    setError('');
    setUploadedFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    handleFileChange(file);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let documentUrl = '';

      // Upload file to Supabase Storage if one was attached
      if (uploadedFile) {
        const supabase = createClient();
        const timestamp = Date.now();
        const safeName = uploadedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const filePath = `${timestamp}_${safeName}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('client-documents')
          .upload(filePath, uploadedFile, { cacheControl: '3600', upsert: false });

        if (uploadError) {
          setError('Failed to upload document. Please try again or email us directly.');
          setLoading(false);
          return;
        }

        const { data: urlData } = supabase.storage
          .from('client-documents')
          .getPublicUrl(uploadData.path);

        documentUrl = urlData?.publicUrl ?? '';
      }

      const naturalMessage = `Hi Betty,

You have a new enquiry from ${fields.name}.

${fields.name} is currently working as ${fields.title} and is looking to move into ${fields.targetRole}. They are interested in your ${fields.service} service.

Here is what they had to say:

"${fields.message}"

You can reach them at ${fields.email}${fields.phone ? ` or by phone on ${fields.phone}` : ''}.${fields.linkedin ? `\n\nTheir LinkedIn profile: ${fields.linkedin}` : ''}${documentUrl ? `\n\nDocument (résumé / CV): ${documentUrl}` : ''}

This message was sent via the contact form on bettyberkelycoaching.com.`;

      const formData = new FormData();
      formData.append('access_key', process.env.NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY || 'YOUR_WEB3FORMS_ACCESS_KEY');
      formData.append('subject', `New Enquiry from ${fields.name} — ${fields.service || 'General Enquiry'}`);
      formData.append('from_name', fields.name);
      formData.append('replyto', fields.email);
      formData.append('message', naturalMessage);
      formData.append('botcheck', '');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setUploadedFile(null);
        setFields({ name: '', email: '', phone: '', title: '', service: '', targetRole: '', linkedin: '', message: '' });
      } else {
        setError(data.message || 'Something went wrong. Please try again or email us directly.');
      }
    } catch {
      setError('Unable to send your message. Please email us directly at info@bettyberkelycoaching.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-plum relative py-24 lg:py-32 overflow-hidden" id="contact">
      <div className="noise-overlay" />
      <div className="max-w-4xl mx-auto px-6 lg:px-12">

        <div ref={headRef} className="reveal-section mb-14">
          <span className="section-index block mb-4" style={{ color: 'rgba(201,168,76,0.5)' }}>
            GET IN TOUCH
          </span>
          <h2
            className="font-fraunces font-medium mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', color: '#F5F0E8', lineHeight: '1.05' }}
          >
            Book Your Free<br />
            <span style={{ color: '#C9A84C', fontStyle: 'italic' }}>Résumé Critique</span>
          </h2>
          <p className="max-w-lg text-base font-light leading-relaxed" style={{ color: 'rgba(245,240,232,0.6)' }}>
            Tell us about your situation. We will review your résumé and outline precisely what needs to change to deliver the results you deserve within 24 hours.
          </p>
        </div>

        {submitted ? (
          <div
            className="text-center py-16 rounded-sm"
            style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}
          >
            <svg className="mx-auto mb-4" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <p className="font-fraunces text-xl font-light" style={{ color: '#F5F0E8' }}>
              Thank you! Your message has been sent successfully.
            </p>
            <p className="mt-3 text-sm font-light" style={{ color: 'rgba(245,240,232,0.5)' }}>
              We will be in touch within 24 hours at{' '}
              <span style={{ color: '#C9A84C' }}>{fields.email || 'your email address'}</span>.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Full Name */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Full Name <span style={{ color: '#C9A84C' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={fields.name}
                onChange={handleChange}
                placeholder="Your full name"
                className="contact-input"
              />
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Email Address <span style={{ color: '#C9A84C' }}>*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={fields.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="contact-input"
              />
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={fields.phone}
                onChange={handleChange}
                placeholder="+1 (555) 000-0000"
                className="contact-input"
              />
            </div>

            {/* Current Title */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Current Job Title <span style={{ color: '#C9A84C' }}>*</span>
              </label>
              <input
                type="text"
                name="title"
                required
                value={fields.title}
                onChange={handleChange}
                placeholder="e.g. Senior Director of Engineering"
                className="contact-input"
              />
            </div>

            {/* Service */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Service Required <span style={{ color: '#C9A84C' }}>*</span>
              </label>
              <select
                name="service"
                required
                value={fields.service}
                onChange={handleChange}
                className="contact-input"
              >
                <option value="" disabled>Select a service…</option>
                <option>Executive Résumé Writing From $695</option>
                <option>LinkedIn Profile Optimisation From $395</option>
                <option>Career Transition Coaching From $450/session</option>
                <option>Interview Preparation From $395/session</option>
                <option>Salary Negotiation Coaching From $395/session</option>
                <option>Cover Letter Writing From $195</option>
                <option>SWOT Analysis $350</option>
                <option>30-60-90 Day Action Plan $425</option>
                <option>Package: Essential $995</option>
                <option>Package: Professional $1,895</option>
                <option>Package: Executive Elite $3,495</option>
                <option>Free Résumé Critique</option>
              </select>
            </div>

            {/* Target Role */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Target Role / Industry <span style={{ color: '#C9A84C' }}>*</span>
              </label>
              <input
                type="text"
                name="targetRole"
                required
                value={fields.targetRole}
                onChange={handleChange}
                placeholder="e.g. VP of Product, Tech industry"
                className="contact-input"
              />
            </div>

            {/* LinkedIn */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                LinkedIn Profile URL
              </label>
              <input
                type="url"
                name="linkedin"
                value={fields.linkedin}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/yourprofile"
                className="contact-input"
              />
            </div>

            {/* Document Upload */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Attach Document
                <span className="ml-2 normal-case tracking-normal font-light text-xs" style={{ color: 'rgba(245,240,232,0.4)' }}>
                  — résumé, CV, or cover letter (PDF, DOC, DOCX, TXT · max 10 MB)
                </span>
              </label>

              {uploadedFile ? (
                <div
                  className="flex items-center justify-between px-4 py-3 rounded-sm"
                  style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5" className="flex-shrink-0">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="text-sm truncate" style={{ color: '#F5F0E8' }}>{uploadedFile.name}</span>
                    <span className="text-xs flex-shrink-0" style={{ color: 'rgba(245,240,232,0.4)' }}>{formatFileSize(uploadedFile.size)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUploadedFile(null)}
                    className="ml-3 flex-shrink-0 p-1 rounded-sm transition-opacity hover:opacity-70"
                    style={{ color: 'rgba(245,240,232,0.5)' }}
                    aria-label="Remove file"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-2 py-8 rounded-sm cursor-pointer transition-all"
                  style={{
                    border: `1px dashed ${dragOver ? 'rgba(201,168,76,0.6)' : 'rgba(201,168,76,0.25)'}`,
                    background: dragOver ? 'rgba(201,168,76,0.06)' : 'rgba(201,168,76,0.02)',
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5" style={{ opacity: 0.6 }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <p className="text-sm font-light" style={{ color: 'rgba(245,240,232,0.5)' }}>
                    Drag &amp; drop or <span style={{ color: '#C9A84C' }}>click to browse</span>
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    className="hidden"
                    onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                  />
                </div>
              )}
            </div>

            {/* Message */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-xs font-mono-custom tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
                Your Goals &amp; Message <span style={{ color: '#C9A84C' }}>*</span>
              </label>
              <textarea
                name="message"
                required
                rows={5}
                value={fields.message}
                onChange={handleChange}
                placeholder="Tell us about your career goals, current challenges, and what you're hoping to achieve…"
                className="contact-input resize-none"
              />
            </div>

            {error && (
              <div className="md:col-span-2 px-4 py-3 rounded-sm text-sm" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', color: '#fca5a5' }}>
                {error}
              </div>
            )}

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="cta-gold px-10 py-4 rounded-sm text-sm font-semibold tracking-wide inline-flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    Sending…
                  </>
                ) : (
                  <>
                    Send My Résumé for Review
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
